import React from "react";
import { validationUtils } from "../validation/Validation";
import { IDriver, IIncidentInfo, IViolationList, ViolationSource } from "../classes/Domain";
import Select, { ValueType } from "react-select";
import { Form, Button, Row, Col } from 'react-bootstrap';
import { QUOTE_QUESTIONS, YES_NO } from "../AppConstants";
import { Rifm } from "rifm";
import Utilities from "../classes/Utilities";
import {
    DL_LICENSE_STATE_OPTIONS,
    EXPIRED_LICENSE_CA_MESSAGE,
    PRIVACY_NOTICE_URL,
} from "../config/StateQuoteConfig";
import {
    ageFromBirthDate,
    yearsExperienceFromBirthDate,
} from "../config/driverLicenseExperience";
import Question from "./Question";
import ViolationPart from "./ViolationPart";

const licenseStatusItems = [
    QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE,
    QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.LEARNERS_PERMIT,
    QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.SUSPENDED,
    QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.EXPIRED,
];

const maritalStatusItems = [
    QUOTE_QUESTIONS.ABOUT_YOU.MARITAL_STATUS.SINGLE,
    QUOTE_QUESTIONS.ABOUT_YOU.MARITAL_STATUS.MARRIED,
];

type DriverOption = { value: string, label: string }

interface IState {
    driver: IDriver;
}

interface IProps {
    updatingStore?: boolean,
    driver: IDriver,
    driverIndex: number,
    isActive: boolean,
    disabled?: boolean,
    errors: any,
    /** TX: show/require age+gender; CA: hide (StateQuoteConfig.requireDriverAgeGender). */
    requireAgeGender: boolean,
    /** Default DL state from quote zip stateCode (Q21). */
    defaultLicenseState: string,
    phoneValue?: string,
    onPhoneChange?: (val: string) => void,
    violationList?: IViolationList | null,
    stateCode?: string,
    onDriverChange(driver: IDriver, index: number): void;
    onDriverDelete(index: number): void;
}

class DriverPart2 extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            driver: this.normalizeDriver(this.props.driver)
        };
    }

    normalizeDriver(driver: IDriver): IDriver {
        return {
            ...driver,
            licenseState: driver?.licenseState || this.props.defaultLicenseState,
            currentLicenseStatus: driver?.currentLicenseStatus
                || QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE,
        } as IDriver;
    }

    /** UI defaults license fields locally; parent must get the same values or Continue validates empty. */
    syncDefaultsToParent = () => {
        const raw = this.props.driver;
        if (!raw) return;
        const needsLicenseState = !raw.licenseState && !!this.props.defaultLicenseState;
        const needsLicenseStatus = !raw.currentLicenseStatus;
        if (!needsLicenseState && !needsLicenseStatus) return;
        this.props.onDriverChange(this.normalizeDriver(raw), this.props.driverIndex);
    };

    componentDidMount() {
        this.syncDefaultsToParent();
    }

    componentDidUpdate(prevProps: IProps) {
        if (prevProps.driver !== this.props.driver
            || prevProps.defaultLicenseState !== this.props.defaultLicenseState) {
            this.syncDefaultsToParent();
        }
    }

    static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
        if (nextProps.driver !== prevState.driver) {
            const driver = {
                ...nextProps.driver,
                licenseState: nextProps.driver?.licenseState || nextProps.defaultLicenseState,
                currentLicenseStatus: nextProps.driver?.currentLicenseStatus
                    || QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE,
            } as IDriver;
            return { driver };
        }
        return null;
    }

    handleFieldChange = (fieldName: keyof IDriver, value: any) => {
        const updatedDriver: IDriver = {
            ...this.state.driver,
            [fieldName]: value
        };

        if (fieldName === "birthDate") {
            const age = ageFromBirthDate(value);
            updatedDriver.age = age;
            updatedDriver.licenseYears = String(yearsExperienceFromBirthDate(value));
        }

        if (fieldName === "accidentsViolations") {
            if (value === QUOTE_QUESTIONS.YES_NO.YES) {
                if (!updatedDriver.incidents || updatedDriver.incidents.length === 0) {
                    const incident: IIncidentInfo = {
                        category: "",
                        violation: "",
                        violationCode: "",
                        violationDate: new Date(),
                        convictionDate: null,
                        incidentDescription: "",
                        source: ViolationSource.ClientOrSystem
                    };
                    updatedDriver.incidents = [incident];
                }
            } else {
                updatedDriver.incidents = [];
            }
        }

        this.setState({ driver: updatedDriver }, () => {
            this.props.onDriverChange(updatedDriver, this.props.driverIndex);
        });
    };

    render() {
        const { driver } = this.state;
        const { requireAgeGender } = this.props;
        const currentLicenseState = DL_LICENSE_STATE_OPTIONS.find(x => x.value === driver?.licenseState)
            || DL_LICENSE_STATE_OPTIONS.find(x => x.value === this.props.defaultLicenseState)
            || null;

        const phoneVal = this.props.phoneValue || "";
        const showViolations = driver?.accidentsViolations === QUOTE_QUESTIONS.YES_NO.YES && !!this.props.violationList;

        return (
            <div className="driver-form-container mb-4" style={{ textAlign: 'left' }}>

                <Row>
                    <Col sm={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-bold">FIRST NAME</Form.Label>
                            <Form.Control
                                type="text"
                                value={driver?.firstName || ""}
                                onChange={(e) => this.handleFieldChange("firstName", e.target.value)}
                                disabled={this.props.updatingStore || this.props.disabled}
                                isInvalid={null != this.props.errors && !driver?.firstName && validationUtils.hasValidationErrorFor(this.props.errors, "driver.firstName")}
                            />
                            {null != this.props.errors && !driver?.firstName && validationUtils.hasValidationErrorFor(this.props.errors, "driver.firstName") && (
                                <div className="text-danger small mt-1">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "driver.firstName")}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col sm={6}>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-bold">LAST NAME</Form.Label>
                            <Form.Control
                                type="text"
                                value={driver?.lastName || ""}
                                onChange={(e) => this.handleFieldChange("lastName", e.target.value)}
                                disabled={this.props.updatingStore || this.props.disabled}
                                isInvalid={null != this.props.errors && !driver?.lastName && validationUtils.hasValidationErrorFor(this.props.errors, "driver.lastName")}
                            />
                            {null != this.props.errors && !driver?.lastName && validationUtils.hasValidationErrorFor(this.props.errors, "driver.lastName") && (
                                <div className="text-danger small mt-1">
                                    {validationUtils.getValidationErrorFor(this.props.errors, "driver.lastName")}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-3">
                    <Form.Label className="text-muted small fw-bold">DATE OF BIRTH</Form.Label>
                    <Rifm
                        accept={/\d/g}
                        mask={10 <= (driver?.birthDate || "").length}
                        format={Utilities.formatDate}
                        value={driver?.birthDate || ""}
                        onChange={(v) => this.handleFieldChange("birthDate", v)}>
                        {({ value, onChange }) => (
                            <Form.Control
                                type="text"
                                placeholder="mm/dd/yyyy"
                                value={value}
                                onChange={onChange}
                                disabled={this.props.updatingStore || this.props.disabled}
                                pattern="[0-9]*"
                                inputMode="numeric"
                                isInvalid={null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "driver.birthDate")}
                            />
                        )}
                    </Rifm>
                    {null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "driver.birthDate") && (
                        <div className="text-danger small mt-1">
                            {validationUtils.getValidationErrorFor(this.props.errors, "driver.birthDate")}
                        </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="text-muted small fw-bold">LICENSE STATE / COUNTRY</Form.Label>
                    <Select
                        options={DL_LICENSE_STATE_OPTIONS as DriverOption[]}
                        value={currentLicenseState}
                        placeholder={"Please select"}
                        onChange={(val: ValueType<DriverOption, false>) => this.handleFieldChange("licenseState", val ? val.value : "")}
                        isSearchable={true}
                        isDisabled={this.props.updatingStore || this.props.disabled}
                    />
                    {null != this.props.errors && !driver?.licenseState && validationUtils.hasValidationErrorFor(this.props.errors, "driver.licenseState") && (
                        <div className="text-danger small mt-1">
                            {validationUtils.getValidationErrorFor(this.props.errors, "driver.licenseState")}
                        </div>
                    )}
                </Form.Group>

                <Question
                    name={`marital-${this.props.driverIndex}`}
                    className="driver-question"
                    lable="Marital Status"
                    items={maritalStatusItems}
                    selectedValue={driver?.maritalStatus || ""}
                    selected={(v) => this.handleFieldChange("maritalStatus", v)}
                    disabled={this.props.updatingStore || this.props.disabled}
                    error={
                        null != this.props.errors && !driver?.maritalStatus && validationUtils.hasValidationErrorFor(this.props.errors, "driver.maritalStatus")
                            ? validationUtils.getValidationErrorFor(this.props.errors, "driver.maritalStatus")
                            : ""
                    }
                />

                <Question
                    name={`license-status-${this.props.driverIndex}`}
                    className="driver-question"
                    lable="License Status"
                    items={licenseStatusItems}
                    selectedValue={driver?.currentLicenseStatus || ""}
                    selected={(v) => this.handleFieldChange("currentLicenseStatus", v)}
                    disabled={this.props.updatingStore || this.props.disabled}
                    error={
                        null != this.props.errors && !driver?.currentLicenseStatus && validationUtils.hasValidationErrorFor(this.props.errors, "driver.currentLicenseStatus")
                            ? validationUtils.getValidationErrorFor(this.props.errors, "driver.currentLicenseStatus")
                            : ""
                    }
                />

                {(this.props.stateCode || "").toUpperCase() === "CA"
                    && driver?.currentLicenseStatus === QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.EXPIRED && (
                        <div className="text-danger small mb-3" role="alert">
                            {EXPIRED_LICENSE_CA_MESSAGE}
                        </div>
                    )}

                {requireAgeGender && (
                    <Question
                        name={`gender-${this.props.driverIndex}`}
                        className="driver-question"
                        lable="Gender"
                        items={[QUOTE_QUESTIONS.ABOUT_YOU.GENDER.MALE, QUOTE_QUESTIONS.ABOUT_YOU.GENDER.FEMALE]}
                        selectedValue={driver?.gender || ""}
                        selected={(v) => this.handleFieldChange("gender", v)}
                        disabled={this.props.updatingStore || this.props.disabled}
                        error={
                            null != this.props.errors && !driver?.gender && validationUtils.hasValidationErrorFor(this.props.errors, "driver.gender")
                                ? validationUtils.getValidationErrorFor(this.props.errors, "driver.gender")
                                : ""
                        }
                    />
                )}

                <Question
                    name={`violations-${this.props.driverIndex}`}
                    className="driver-question"
                    lable="any accidents or traffic violations in the last 3 years?"
                    items={[YES_NO.YES, YES_NO.NO]}
                    selectedValue={driver?.accidentsViolations || ""}
                    selected={(v) => this.handleFieldChange("accidentsViolations", v)}
                    disabled={this.props.updatingStore || this.props.disabled}
                    error={
                        null != this.props.errors && !driver?.accidentsViolations && validationUtils.hasValidationErrorFor(this.props.errors, "driver.accidentsViolations")
                            ? validationUtils.getValidationErrorFor(this.props.errors, "driver.accidentsViolations")
                            : ""
                    }
                />

                {showViolations && (
                    <Form.Group className="mb-3 vehicle-row">
                        <Row>
                            <Col sm={8} className="justify-content-center d-flex">Incident</Col>
                            <Col sm={3} className="justify-content-center d-flex">Violation Date</Col>
                            <Col sm={1} className="justify-content-center d-flex"></Col>
                        </Row>
                        <ViolationPart
                            updatingStore={this.props.updatingStore}
                            driver={driver}
                            driverIndex={this.props.driverIndex}
                            stateCode={this.props.stateCode}
                            violationList={this.props.violationList}
                            onDriverChange={(updated, index) => {
                                // Keep DriverPart2 local state in sync (Question Yes/No + incidents).
                                this.setState({ driver: updated }, () => {
                                    this.props.onDriverChange(updated, index);
                                });
                            }}
                            errors={this.props.errors}
                        />
                    </Form.Group>
                )}

                {this.props.driverIndex === 0 && (
                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted small fw-bold">
                            PHONE NUMBER <span className="fw-normal" style={{ fontStyle: "italic" }}>*required to continue with quote</span>
                        </Form.Label>
                        <Rifm
                            accept={/\d/g}
                            mask={true}
                            format={Utilities.formatPhone}
                            value={phoneVal}
                            onChange={this.props.onPhoneChange || (() => { })}
                        >
                            {({ value, onChange }) => (
                                <Form.Control
                                    type="text"
                                    placeholder="(___)___-____"
                                    value={value}
                                    onChange={onChange}
                                    maxLength={14}
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    disabled={this.props.updatingStore || this.props.disabled}
                                    isInvalid={null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "contactInfo.phone")}
                                />
                            )}
                        </Rifm>
                        {null != this.props.errors && validationUtils.hasValidationErrorFor(this.props.errors, "contactInfo.phone") && (
                            <div className="text-danger small mt-1">
                                {validationUtils.getValidationErrorFor(this.props.errors, "contactInfo.phone")}
                            </div>
                        )}
                        <div className="text-muted mt-2" style={{ fontStyle: "italic" }}>
                            By submitting your phone number and/or email address you agree to receive calls
                            and notification from us regarding this quote - for more details visit our{" "}
                            <a href={PRIVACY_NOTICE_URL} target="_blank" rel="noreferrer">privacy notice</a>
                        </div>
                    </Form.Group>
                )}

                {this.props.driverIndex > 0 && (
                    <div className="text-end mt-2">
                        <Button
                            variant="danger"
                            size="sm"
                            className="btn-remove"
                            onClick={() => { this.props.onDriverDelete(this.props.driverIndex) }}
                            disabled={this.props.updatingStore}
                        >
                            <i className="fas fa-trash me-1"></i> Remove Driver
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

export default DriverPart2
